import { useState } from 'react';
import { Link as MuiLink } from '@mui/material';
import { AccountBox, Mail, Phone, Person, LocationOn } from '@mui/icons-material';
import { useMutation, useQuery } from '@apollo/client/react';
import { GET_USER, UPDATE_ADDRESS, UPDATE_EMAIL, UPDATE_LOGIN, UPDATE_NAME, UPDATE_PHONE } from '../user.graphql';
import { Address, User } from '../../interfaces';
import { Card } from './Card';
import { InfoRow, formatAddress, formatPhone } from './InfoRow';
import { UpdateNameDialog } from './UpdateNameDialog';
import { UpdateValueDialog } from '../../components/UpdateValueDialog';
import { UpdateAddressDialog } from '../../components/UpdateAddressDialog';
import LoadingSpinner from '../../components/LoadingSpinner';

interface Props {
  userId: number;
  onLoginAlreadyUsed(): void;
}

export function ProfilePanel({ userId, onLoginAlreadyUsed }: Props) {
  const { loading, error, data } = useQuery<{ user: User }>(GET_USER, { variables: { id: userId } });

  const [updateLogin] = useMutation<any, any>(UPDATE_LOGIN, { onError: onLoginAlreadyUsed });
  const [updateName] = useMutation<any, any>(UPDATE_NAME);
  const [updateEmail] = useMutation<any, any>(UPDATE_EMAIL, { onError: onLoginAlreadyUsed });
  const [updatePhone] = useMutation<any, any>(UPDATE_PHONE);
  const [updateAddress] = useMutation<any, any>(UPDATE_ADDRESS);

  const [openDialog, setOpenDialog] = useState<null | 'loginId' | 'name' | 'email' | 'phone' | 'address'>(null);
  const close = () => setOpenDialog(null);

  if (error) return <span>Feil! {error.message}</span>;
  if (loading || !data) return <LoadingSpinner />;
  const user = data.user;

  const phoneFormatted = formatPhone(user.phone);
  const addressLine = formatAddress(user.address);

  return (
    <Card title="Detaljer">
      <InfoRow
        icon={<AccountBox />}
        label="Brukernavn"
        value={user.loginId}
        onEdit={() => setOpenDialog('loginId')}
      />
      <InfoRow
        icon={<Person />}
        label="Navn"
        value={user.displayName || `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()}
        onEdit={() => setOpenDialog('name')}
      />
      <InfoRow
        icon={<Mail />}
        label="E-post"
        value={user.email}
        valueRender={(v) => (
          <MuiLink href={`mailto:${v}`} underline="hover">
            {v}
          </MuiLink>
        )}
        onEdit={() => setOpenDialog('email')}
      />
      <InfoRow icon={<Phone />} label="Telefon" value={phoneFormatted} onEdit={() => setOpenDialog('phone')} />
      <InfoRow icon={<LocationOn />} label="Adresse" value={addressLine} onEdit={() => setOpenDialog('address')} />

      <UpdateValueDialog
        open={openDialog === 'loginId'}
        onClose={close}
        title="Endre brukernavn"
        label="Brukernavn"
        type="email"
        value={user.loginId ?? ''}
        update={(v: string) => updateLogin({ variables: { input: { id: userId, loginId: v } } })}
      />
      <UpdateNameDialog
        open={openDialog === 'name'}
        onClose={close}
        firstName={user.firstName ?? ''}
        lastName={user.lastName ?? ''}
        update={(firstName, lastName) => updateName({ variables: { input: { id: userId, firstName, lastName } } })}
      />
      <UpdateValueDialog
        open={openDialog === 'email'}
        onClose={close}
        title="Endre e-post"
        label="E-post"
        type="email"
        value={user.email ?? ''}
        update={(v: string) => updateEmail({ variables: { input: { id: userId, email: v } } })}
      />
      <UpdateValueDialog
        open={openDialog === 'phone'}
        onClose={close}
        title="Endre telefonnummer"
        label="Telefonnummer"
        value={user.phone ?? ''}
        update={(v: string) => updatePhone({ variables: { input: { id: userId, phone: v } } })}
      />
      {user.address && (
        <UpdateAddressDialog
          open={openDialog === 'address'}
          onClose={close}
          address={user.address}
          updateAddress={(addr: Address) => {
            const { id: _ignored, ...rest } = addr;
            return updateAddress({ variables: { input: { id: userId, ...rest } } });
          }}
        />
      )}
    </Card>
  );
}
